import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from '@/authorization';
import { productActionDefinitions } from '@/config/productRegistry';
import type { ProductActionId, RegistryProduct } from '@/types/productRegistry';

interface ProductActionsMenuProps {
  product: RegistryProduct;
  onAction: (actionId: ProductActionId, product: RegistryProduct) => void;
}

export function ProductActionsMenu({
  product,
  onAction,
}: ProductActionsMenuProps) {
  const { t } = useTranslation('entity-portal');
  const { hasPermission } = useAuthorization();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const visibleActions = productActionDefinitions.filter((action) => {
    if (!action.permission) {
      return true;
    }

    return hasPermission(action.permission);
  });

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (actionId: ProductActionId) => {
    onAction(actionId, product);
    handleClose();
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={t('productRegistry.productRegistryRowActions', { product: product.id })}
        onClick={handleOpen}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {visibleActions.map((action) => (
          <MenuItem
            key={action.id}
            onClick={() => handleSelect(action.id)}
            sx={
              'destructive' in action && action.destructive
                ? { color: 'error.main' }
                : undefined
            }
          >
            {t(action.labelKey)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
